import { HCO_DEFAULT_SELECTED_FIELDS, TIN_DEFAULT_SELECTED_FIELDS, GEO_DEFAULT_SELECTED_FIELDS } from './ClaimsReporting.constants';

 export const formatTimePeriod = (value: string) =>
   value
     .replace(/Last(\d+)/g, 'Last $1')
     .replace(/([a-z])([A-Z])/g, '$1 $2')
     .replace(/(\d)([A-Za-z])/g, '$1 $2')
     .replace(/\s+/g, ' ')
     .trim();

export const formatTimePeriod2 = (value: string) => value.replace(/\s+/g, '');

export const formatLOBOptions = (value: string) => {
  const lobMapping: { [key: string]: string } = {
    'EI': 'Commercial',
    'EXC' : 'Individual Exchange',
    'MR': 'Medicare',
    'CS': 'Medicaid'
    };

  return lobMapping[value] || value;
};

export interface ReportValidationErrors {
  reportName?: string;
  timePeriod?: string;
  fromDate?: string;
  toDate?: string;
  selectedFields?: string;
}

export const VALIDATION_MESSAGES = {
  REPORT_NAME_REQUIRED: 'Report name is required.',
  REPORT_NAME_MIN_LENGTH: 'Report name must be between 1 to 35 characters.',
  TIME_PERIOD_REQUIRED: 'Time period is required.',
  FROM_DATE_REQUIRED: 'From date is required.',
  TO_DATE_REQUIRED: 'To date is required.',
  DATE_RANGE_MAX_ONE_YEAR: 'Date range must not exceed 1 year.',
  TO_DATE_FUTURE: 'To date cannot be a future date.',
  FROM_DATE_AFTER_TO_DATE: 'From date must not be after To date.',
  SELECTED_FIELDS: 'Selected field should be between 1 and 50.',
};

export const validateReportParameters = (reportData: {
  reportName: string;
  timePeriod: string;
  fromDate: string;
  toDate: string;
  selectedFields: string[];
  field: 'save' | 'generate';
}): ReportValidationErrors => {
  const errors: ReportValidationErrors = {};
  const { reportName, timePeriod, fromDate, toDate, selectedFields, field } = reportData;

  // Report Name validation
  if ((!reportName || reportName.trim() === '') && field === 'save') {
    errors.reportName = VALIDATION_MESSAGES.REPORT_NAME_REQUIRED;
  } else if ((reportName.trim().length < 1 || reportName.trim().length > 35) && field === 'save') {
    errors.reportName = VALIDATION_MESSAGES.REPORT_NAME_MIN_LENGTH;
  }

  // Time Period validation
  if (!timePeriod) {
    errors.timePeriod = VALIDATION_MESSAGES.TIME_PERIOD_REQUIRED;
  }

  // Custom Range date validations
  if (timePeriod === 'CustomRange') {
    if (!fromDate) {
      errors.fromDate = VALIDATION_MESSAGES.FROM_DATE_REQUIRED;
    }
    if (!toDate) {
      errors.toDate = VALIDATION_MESSAGES.TO_DATE_REQUIRED;
    }
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      from.setHours(0, 0, 0, 0);
      const to = new Date(toDate);
      to.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (to > today) {
        errors.toDate = VALIDATION_MESSAGES.TO_DATE_FUTURE;
      }

      if (from > to) {
        errors.fromDate = VALIDATION_MESSAGES.FROM_DATE_AFTER_TO_DATE;
      }

      const oneYearFromStart = new Date(from);
      oneYearFromStart.setFullYear(oneYearFromStart.getFullYear() + 1);
      if (to > oneYearFromStart) {
        errors.toDate = errors.toDate || VALIDATION_MESSAGES.DATE_RANGE_MAX_ONE_YEAR;
      }
    }
  }

  // Selected Fields validation
  if (!selectedFields || selectedFields.length === 0 || selectedFields.length > 50) {
    errors.selectedFields = VALIDATION_MESSAGES.SELECTED_FIELDS;
  }

  return errors;
};

const appendSelectedFieldsClause = (query: string, selectedFields?: string[]): string =>
  selectedFields && selectedFields.length > 0 ? `${query} ${selectedFields.join(', ')}` : query;

const appendLobClause = (query: string, lob?: string): string =>
  lob && lob !== 'All' ? `${query} for Line of Business ${lob}` : query;

const appendProviderSysKeyClause = (query: string, providerSysKey?: string): string =>
  providerSysKey ? `${query} for providerSysKey ${providerSysKey}` : query;

const appendTinClause = (query: string, taxIds?: { Tin: string }[]): string => {
  if (!taxIds || taxIds.length === 0 || taxIds[0].Tin === 'All') {
    return query;
  }
  const label = taxIds.length === 1 ? 'TIN' : 'TINs';
  return `${query} for ${label} ${taxIds}`;
};

const appendGeoClause = (
  query: string,
  { region, markets, states, providerSysKey }: { region?: string; markets?: string[]; states?: string[]; providerSysKey?: string }
): string => {
  if (!(region || markets || states)) {
    return query;
  }
  let result = query;
  if (region && !providerSysKey) {
    result += ` for Region ${region}`;
  }
  if (states && states.length > 0) {
    result += ` for State ${states.join(', ')}`;
  }
  if (markets && markets.length > 0) {
    result += ` for Market ${markets.join(', ')}`;
  }
  return result;
};

const appendTimePeriodClause = (query: string, timePeriod?: string, fromDate?: string, toDate?: string): string => {
  if (timePeriod === 'CustomRange' || timePeriod === 'Custom Range') {
    return `${query} from ${fromDate} to ${toDate}`;
  }
  if (timePeriod) {
    return `${query} for ${timePeriod}`;
  }
  return query;
};

export const generateNaturalQueryParameter = (payload: any) => {
  const { selectedFields, lob, providerSysKey, taxIds, region, markets, states, fromDate, toDate, timePeriod } =
    payload;

  let queryInitialTemplate = 'Give data for';
  queryInitialTemplate = appendSelectedFieldsClause(queryInitialTemplate, selectedFields);
  queryInitialTemplate = appendLobClause(queryInitialTemplate, lob);
  queryInitialTemplate = appendProviderSysKeyClause(queryInitialTemplate, providerSysKey);
  queryInitialTemplate = appendTinClause(queryInitialTemplate, taxIds);
  queryInitialTemplate = appendGeoClause(queryInitialTemplate, { region, markets, states, providerSysKey });
  queryInitialTemplate = appendTimePeriodClause(queryInitialTemplate, timePeriod, fromDate, toDate);
  queryInitialTemplate += ` by ${getViewByLabelMapping(payload.viewReportsBy || 'DOS')}`;
  return queryInitialTemplate;
};

export const formatNumberWithCommas = (value: unknown): string => {
  if (typeof value === 'number' && isFinite(value)) {
    return Number(value).toLocaleString('en-US');
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const num = Number(value);
    if (isFinite(num)) {
      return num.toLocaleString('en-US');
    }
  }
  return String(value ?? '');
};

export const getViewByLabelMapping = (viewBy: 'DOP' | 'DOS'): string => {
  const mapping: { [key in 'DOP' | 'DOS']: string } = {
    DOP: 'Date of Processing',
    DOS: 'Date of Service',
  };
  return mapping[viewBy];
};

// Method to check if Application is in HCO Flow, Single Tin Flow, Multi Tin Flow or Geo Flow.
// Identyfing from insightData object which is passed from the card component.

export const appFlowIdentifier = (insightData: any): 'HCO' | 'TIN' | 'GEO' => {
  if (insightData?.hcoName && insightData?.providerSysKey) {
    return 'HCO';
  }
  if (insightData?.taxIds && insightData?.taxIds.length > 0 && !insightData?.providerSysKey && insightData?.taxIds[0]?.Tin !== 'All') {
    return 'TIN';
  }
  if (insightData?.region || insightData?.markets || insightData?.states) {
    return 'GEO';
  }
  return 'HCO'; // Default to HCO if none of the above conditions are met
};

export const getDefaultSelectedFields = (appFlowIdentifier: 'HCO' | 'TIN' | 'GEO') => {
  switch (appFlowIdentifier) {
    case 'HCO':
      return HCO_DEFAULT_SELECTED_FIELDS;
    case 'TIN':
      return TIN_DEFAULT_SELECTED_FIELDS;
    case 'GEO':
      return GEO_DEFAULT_SELECTED_FIELDS;
    default:
      return [...HCO_DEFAULT_SELECTED_FIELDS];
  }
};